/* eslint-disable @typescript-eslint/no-use-before-define */
import { getCookie } from '@visa/lib-cookie';
import { b64Encode } from 'common/base64';
import { getAppInstance, getPlatformType, isMobileSafari, isPhoneBrowser } from 'common/browser';
import { decodeCheckoutResponse } from 'common/checkout-utils';
import getParamFromUrl from 'common/get-param';
import { isAddressValidated } from 'communicator/utils/address';
import isTrue from 'communicator/utils/boolean';
import { getCountryCodeByISO } from 'communicator/utils/country';
import { encryptData } from 'communicator/utils/encrypt';
import { getTopOrigin } from 'communicator/utils/get-top-origin';
import getIdFromUrl from 'communicator/utils/getIdFromUrl';
import { CardListRequestPayload } from 'types/api/card-list';
import {
  ConsumerNationalIdentiferData,
  OrchestratorCheckoutEnrollRequest
} from 'types/api/checkout';
import { VUser } from 'types/api/create-account';
import {
  BillingConstraints,
  MerchantConfig,
  PaymentInstrument,
  PutIntentRequestPayload,
  SetIntentRequestPayload,
  ShippingAddress,
  ShippingConstraints,
  ThreeDsSetupEnumerated,
  ThreeDSSetupOptions
} from 'types/api/intent';
import {
  AssuranceData,
  CountryCode,
  InitialVInitRequest,
  ReviewButtonAction,
  SRCConfigs,
  VInitRequest
} from 'types/data';
import {
  AuthenticationMethod,
  Consumer,
  DpaTransactionOptions,
  MethodAttributes,
  PayloadTypeIndicator
} from 'types/emvco';
import {
  AuthenticateApiRequestPayload,
  AuthenticateData,
  IssuerOTPRequestConfigs
} from 'types/visa-sdk/authenticate';
import {
  BankAppConfig,
  CardArtConfig,
  CheckoutRequestMetadata,
  ComplianceType,
  CvvConfig,
  FidoConfig
} from 'types/visa-sdk/checkout';
import { CommunicatorState, getState } from './index';
// The import order here matters - we get an error if push-events appears in alphabetical order
// eslint-disable-next-line import/order
import pushEvents from 'communicator/internal/push-events';

// istanbul ignore if
if (process.env.NODE_ENV !== 'production' && !window.location.href.includes('/communicator')) {
  throw new Error('Cannot access selector from outside of the communicator frame.');
}

export function getMeta() {
  return getState().meta;
}

export function getIsSPCSupported() {
  return getMeta().isSPCSupported;
}

export function getProfile() {
  return getState().profile;
}

/* START GENAI@GHCOPILOT */

export function getProofState() {
  return getState().proofState;
}

/* END GENAI@GHCOPILOT */

export function getIsCheckoutDataReady() {
  return getMeta().isCheckoutDataReady;
}

export function getCorrelationId() {
  return getMeta().correlationId;
}

export function getOrgId() {
  return getMeta().orgId;
}

/* START GENAI@GHCOPILOT */

export function getVppsOrigin() {
  return getMeta().vppsOrigin;
}

export function getIsVdiDisabled() {
  return getMeta().isVdiDisabled;
}

/* END GENAI@GHCOPILOT  */

export function getThmProfileHint() {
  return getMeta().thmProfileHint;
}

export function getPaymentInstrument() {
  return getProfile().paymentInstrument;
}

export function getShippingAddress() {
  return getProfile().shippingAddress;
}

export function getMaskedCards() {
  return getProfile().maskedCards;
}

export function getMaskedShippingAddresses() {
  return getProfile().maskedShippingAddresses;
}

export function getSelectedCardId() {
  return getMeta().selectedCardId;
}

export function getCheckoutData() {
  return getState().config.checkout;
}

export function getCheckoutCustomData() {
  return getCheckoutData()?.customData;
}

export function getInitData() {
  return getState().config.init;
}

export function getInitCustomData() {
  return getInitData()?.customData;
}

export function getApiKey() {
  const initData = getInitData();
  return getInitCustomData()?.apikey ?? initData?.srcInitiatorId ?? '';
}

export function getIntegrationType() {
  return getMeta().integrationType;
}

export function getDpaTransactionOptions(): DpaTransactionOptions | undefined {
  // VCO-16051: we explicitly don't care about dpaTransactionOptions passed to checkout()
  return getInitData()?.dpaTransactionOptions;
}

export function getClientId() {
  const initCustomData = getInitCustomData();
  return (
    initCustomData?.vInitRequest?.clientId ??
    initCustomData?.merchantConfig?.clientId ??
    initCustomData?.merchantConfig?.srcDpaId
  );
}

export function getExternalClientId() {
  return (
    getInitCustomData()?.vInitRequest?.externalClientId ??
    getInitCustomData()?.merchantConfig?.externalClientId ??
    getInitData()?.srciDpaId ??
    ''
  );
}

export function getExternalProfileId() {
  return getInitCustomData()?.merchantConfig?.externalProfileId || 'SYSTEMDEFAULT';
}

export function getIsSplunkLoggingEnabled() {
  return getMeta().isSplunkLoggingEnabled;
}

// START GENAI@GHCOPILOT
export function getSrcDpaId() {
  const initData = getInitData();
  return initData?.srcDpaId || initData?.dpaData?.srcDpaId || '';
}

// END GENAI@GHCOPILOT

export function getVisitId() {
  return getMeta().visitId;
}

export function getVIntentId() {
  return getMeta().vIntentId;
}

export function getIsVisaSRCi() {
  if (!getInitData()) {
    return Boolean(getParamFromUrl(window.location.href, 'visaSrci'));
  }

  return Boolean(getInitCustomData());
}

export function getNamespace() {
  return getState().meta.namespace;
}

export function getDcfExperience(): 'integrated' | null {
  return getDpaTransactionOptions()?.customInputData?.checkoutOrchestrator === 'merchant'
    ? 'integrated'
    : null;
}

export function getSessionId() {
  return getState().meta.sessionId;
}

export function getThreeDsUrl() {
  return getState().meta.threeDsUrl;
}

export function getUser() {
  return getState().user;
}

export function getTncStatus() {
  return getUser()?.tnc?.status ?? null;
}

export function getTncEntryInfo() {
  return getUser()?.tnc?.tncEntryInfo;
}

export function getAccessToken() {
  return getUser()?.accessToken;
}

export function getFullAccessToken() {
  const accessToken = getAccessToken();
  return accessToken?.isPartial ? undefined : accessToken?.token;
}

/* START GENAI@GHCOPILOT */

export function getTokenServerState() {
  return getMeta().serverState?.token;
}

export function getXAuthCode() {
  return getUser()?.xAuthCode;
}

export function getIsCardFirstFlow() {
  const accessToken = getAccessToken();
  // Visa SRCi will send isCardFirstFlow. Otherwise, if full accessToken is not
  // present, it is a new user flow
  return getCheckoutCustomData()?.isCardFirstFlow ?? (!accessToken?.token || accessToken.isPartial);
}

export function getIsUserLocked() {
  return getUser()?.isLocked;
}

export function getUserLocale() {
  return getUser()?.locale;
}

export function getUserCountry() {
  return getUser()?.country;
}

export function getUserEmail() {
  return getUser()?.email;
}

export function getUserPhone() {
  return getUser()?.phone;
}

export function getIdentityType() {
  return getUser()?.identityType;
}

export function getIdValidationSessionId() {
  return getUser()?.idValidationSessionId;
}

export function getFlow() {
  return getState().flow;
}

export function getIsNonVisaRecognizedUser(amrCookie?: string[]) {
  const otpProvider = getMeta().otpProvider;
  return Boolean(
    // VCO-14507 - Determine if external idToken was generated from OTP or
    // cookie auth. If OTP, we want to avoid a second OTP stepup.
    amrCookie &&
      ['cookie', 'email_otp', 'sms_otp', 'swk'].includes(amrCookie[0]) &&
      // `otpProvider` may be set to VISA if Visa performed OTP, OR if the user
      // is recognized by cookie. If another network performed OTP, we know the
      // user is not considered "recognized" by cookie.
      (!otpProvider || otpProvider === 'VISA')
  );
}

export function getPutIntentResponse() {
  return getState().putIntentResponse;
}

export function getUnBindAppInstance() {
  const user = getUser();
  return !user?.isRecognizedUser && !getComplianceResource('REMEMBER_ME');
}

export function getFeatureToggles() {
  return getState().featureToggles;
}

export function getIsFeatureToggleEnabled(toggle: keyof CommunicatorState['featureToggles']) {
  return getState().featureToggles[toggle];
}

export function getComplianceResource(type: ComplianceType) {
  return Boolean(
    getCheckoutData()?.complianceSettings?.complianceResources.find(
      complianceType => complianceType.complianceType === type
    )
  );
}

export function getVUserForCreateAccount(): VUser {
  const prefillData = getCheckoutCustomData()?.prefillData;

  const vuser: VUser = {
    countryCode: getUserCountry() ?? 'US',
    defaultTimezone: '',
    emailAddress: getUserEmail(),
    languageCode: getUserLocale() ?? 'en-US',
    phone: prefillData?.phone,
    role: 'CONSUMER' as const
  };

  // if vUser contains masked values for firstName and lastName, the call to
  // /consumer-api/v2/account/ARMGUID will fail. If masked, they will be
  // excluded from vUser and BE will fetch this value internally from staging
  if (prefillData?.firstName && !prefillData.firstName.includes('*')) {
    vuser.firstName = prefillData.firstName;
  }

  if (prefillData?.lastName && !prefillData.lastName.includes('*')) {
    vuser.lastName = prefillData.lastName;
  }

  return vuser;
}

export function getCustomFlowType() {
  const dpaTransactionOptions = getDpaTransactionOptions();

  switch (dpaTransactionOptions?.customInputData?.customFlowType?.toLowerCase()) {
    case 'paymentsetting':
      return 'paymentsetting';
    case 'withincheckout':
      return 'withincheckout';
    default:
      return null;
  }
}

export function getThreeDsPreference(): ThreeDsSetupEnumerated | undefined {
  return (
    getInitData()?.dpaData?.dpaThreeDsPreference ?? getDpaTransactionOptions()?.threeDsPreference
  );
}

export function getMerchantConfig(): MerchantConfig {
  const merchantConfigFromSdk = getInitCustomData()?.merchantConfig;

  if (merchantConfigFromSdk) {
    return merchantConfigFromSdk;
  }

  const dpaData = getInitData()?.dpaData;
  const dpaTransactionOptions = getDpaTransactionOptions();
  const externalClientId = getExternalClientId();

  const merchantConfig: MerchantConfig = {
    consumerEmailAddressRequested: dpaTransactionOptions?.consumerEmailAddressRequested,
    consumerNameRequested: dpaTransactionOptions?.consumerNameRequested,
    consumerPhoneNumberRequested: dpaTransactionOptions?.consumerPhoneNumberRequested,
    dataLevel: dpaTransactionOptions?.payloadTypeIndicator ?? 'SUMMARY',
    displayName: dpaData?.dpaPresentationName,
    merchantCountryCode: dpaTransactionOptions?.merchantCountryCode,
    threeDSSetup: {
      threeDSActive: getThreeDsPreference() === 'ONBEHALF'
    },
    websiteUrl: dpaData?.dpaUri
  };

  // only include externalClientId when truthy to prevent intent request 400
  if (externalClientId) {
    merchantConfig.externalClientId = externalClientId;
  }

  if (dpaTransactionOptions) {
    merchantConfig.billingConstraints = {
      acceptedRegions: dpaTransactionOptions.dpaAcceptedBillingCountries?.length
        ? {
            region: dpaTransactionOptions.dpaAcceptedBillingCountries.map(country => ({
              countryCode: country
            }))
          }
        : undefined,
      collectBillingAddress:
        dpaTransactionOptions.dpaBillingPreference === 'NONE'
          ? 'CNTRY'
          : dpaTransactionOptions.dpaBillingPreference === 'POSTAL_COUNTRY'
            ? 'POSTALCNTRY'
            : 'FULL'
    };

    merchantConfig.shippingConstraints = {
      acceptedRegions: dpaTransactionOptions.dpaAcceptedShippingCountries?.length
        ? {
            region: dpaTransactionOptions.dpaAcceptedShippingCountries.map(country => ({
              countryCode: country
            }))
          }
        : undefined,
      collectShipping: dpaTransactionOptions.dpaShippingPreference !== 'NONE'
    };

    if (dpaTransactionOptions.paymentOptions?.[0]) {
      merchantConfig.tokenizationSetup = {
        enableTokenization: !dpaTransactionOptions.paymentOptions[0].dpaPanRequested,
        tokenCryptogramType: dpaTransactionOptions.paymentOptions[0].dynamicDataType
      };
    }
  }

  return merchantConfig;
}

export function getButtonAction(): ReviewButtonAction | undefined {
  const vInitRequestFromSdk = getInitCustomData()?.vInitRequest;
  const dpaTransactionOptions = getDpaTransactionOptions();
  return vInitRequestFromSdk?.paymentRequest.buttonAction || dpaTransactionOptions?.reviewAction;
}

export function getVInitRequest(): Partial<VInitRequest> {
  const vInitRequestFromSdk = getInitCustomData()?.vInitRequest;

  if (vInitRequestFromSdk) {
    return vInitRequestFromSdk;
  }

  // For non-visa SRCi, map init data to vInitRequest

  const dpaTransactionOptions = getDpaTransactionOptions();
  const merchantConfig = getMerchantConfig();

  return {
    ...merchantConfig,
    intialVinit: {} as unknown as InitialVInitRequest,
    payment: {
      billingCountries: dpaTransactionOptions?.dpaAcceptedBillingCountries,
      collectBillingAddress: merchantConfig.billingConstraints?.collectBillingAddress
    },
    paymentRequest: {
      buttonAction: dpaTransactionOptions?.reviewAction ?? 'CONTINUE',
      currencyCode: dpaTransactionOptions?.transactionAmount?.transactionCurrencyCode,
      orderId: dpaTransactionOptions?.merchantOrderId,
      total: dpaTransactionOptions?.transactionAmount?.transactionAmount
    },
    shipping: {
      acceptedRegions: dpaTransactionOptions?.dpaAcceptedShippingCountries,
      collectShipping: merchantConfig.shippingConstraints?.collectShipping ? 'true' : 'false'
    }
  };
}

export function getPartialSelectedCard(): Partial<PaymentInstrument> | null {
  const piId = getSelectedCardId();
  const maskedCards = getMaskedCards();

  // depending on flow/FTs, either maskedCards or paymentInstrument will be present
  if (!maskedCards) {
    const paymentInstruments = getPaymentInstrument();
    const paymentInstrument = paymentInstruments?.find(pi => pi.id === piId);

    if (!paymentInstrument) {
      return null;
    }

    return {
      cardArts: paymentInstrument.cardArts,
      cardDisplay: paymentInstrument.cardDisplay,
      lastFourDigits: paymentInstrument.lastFourDigits,
      paymentType: paymentInstrument.paymentType
    };
  }

  const card = maskedCards.find(card => card.srcDigitalCardId === piId);

  if (!card) {
    return null;
  }

  return {
    cardArts: {
      cardArt: [
        {
          baseImageFileName: card.digitalCardData.artUri,
          height: card.digitalCardData.artHeight,
          imageUrl: card.digitalCardData.artUri,
          width: card.digitalCardData.artWidth
        }
      ]
    },
    cardDisplay: card.paymentCardType,
    lastFourDigits: card.panLastFour,
    paymentType: {
      cardBrand: 'VISA',
      cardType: card.paymentCardType
    }
  };
}

export function getSrcConfigs(): SRCConfigs {
  const dpaTransactionOptions = getDpaTransactionOptions();
  const initData = getInitData();
  const checkoutData = getCheckoutData();
  const initCustomData = getInitCustomData();
  const userProfile = getUser();
  const accessToken = getAccessToken();
  const customFlowType = getCustomFlowType();
  const prefillData = getCheckoutCustomData()?.prefillData;
  const flow = getFlow();

  const isComplianceTncPassed = Boolean(getComplianceResource('TERMS_AND_CONDITIONS'));
  const tncStatus = getTncStatus();

  // TODO: complete getSrcConfigs
  const srcConfigs: SRCConfigs = {
    apikey: getApiKey(),
    // TODO: campaignGuid comes from /campaign api?
    campaignGuid: null,
    // TODO: verify channel is always "Web"
    channel: 'Web',
    consumerNationalIdentifier: getIsFeatureToggleEnabled('enableCpfSupportBrazil')
      ? getCheckoutData()?.consumer?.nationalIdentifier
      : undefined,
    consumerNationalIdentifierRequested: dpaTransactionOptions?.consumerNationalIdentifierRequested,
    cookieCountry: (getCookie('country_preference') as CountryCode | null) ?? null,
    cookieLocale: getCookie('locale') ?? null,
    correlationId: getCorrelationId(),
    countryCode: dpaTransactionOptions?.merchantCountryCode,
    // TODO: dataLayer
    dataLayer: {},
    // TODO: dcfActionCode comes from DCF/VSB
    dcfActionCode: flow.dcfActionCode,
    dpaData: initData?.dpaData,
    dpaTransactionOptions,
    enabledFeatureToggles: getState().featureToggles,
    eventSource: 'LightboxTW',
    isCardFirstFlow: getIsCardFirstFlow(),
    isCookieConsentEnabled: initCustomData?.isCookieConsentEnabled,
    isERMCookieRefreshed: userProfile?.flowType === 'erm',
    isSignedOut: !userProfile,
    isSplunkLoggingEnabled: getIsSplunkLoggingEnabled(),
    isSSICookieRefreshed: userProfile?.flowType === 'ssi',
    locale: dpaTransactionOptions?.dpaLocale,
    merchantConfig: getMerchantConfig(),
    openvv: !getIsVisaSRCi(),
    // Need to pass non-sensitive card info to be rendered in DCF
    partialSelectedCard: getPartialSelectedCard(),
    paymentInstrumentType: getIsFeatureToggleEnabled('enableComboCardSupportBrazil')
      ? getCheckoutData()?.dpaTransactionOptions?.customInputData?.paymentCardTypeSelected
      : undefined,
    payMode: initCustomData?.reviewAction?.toUpperCase() === 'PAY',
    policyVersion: initCustomData?.policyVersion,
    profileCountry: getUserCountry() ?? null,
    profileLocale: getUserLocale() ?? null,
    putIntentResponse: getPutIntentResponse(),
    routeBeforeLaunchDcf: getCheckoutCustomData()?.routeBeforeLaunchDcf,
    // TODO: have to update vsb script to send this
    sdkCerts: initCustomData?.sdkCerts,
    selectedCardId: getSelectedCardId(),
    sessionId: getSessionId(),
    showVdcpIno: true,
    stepUp: {
      grant: flow.grant ?? false
    },
    thmProfileHint: getThmProfileHint(),
    tokens: {
      // TODO: are refreshToken/tokenExpires properties a thing
      expiresIn: accessToken?.expiresIn,
      tncEntryInfo: getTncEntryInfo(),
      xAuthCode: getXAuthCode()
    },
    toRoute: flow.toRoute,
    transfer: {
      addCardInfo: flow.addCardInfo
    },
    userInfo: {
      accountType: 'PASSWORD',
      alwaysRememberMe: Boolean(getComplianceResource('REMEMBER_ME')),
      assuranceData: checkoutData?.assuranceData,
      auth_level: accessToken?.authLevel,
      consumerPresent: Boolean(accessToken?.token && !accessToken.isPartial),
      countryCode: getCountryCodeByISO(getUserCountry()),
      customFlowType,
      email: getUserEmail(),
      emailHash: userProfile?.emailHash,
      flowType: userProfile?.flowType ?? 'standard',
      isComplianceTncPassed,
      isLocked: getIsUserLocked(),
      isLoggedInOtpUser: userProfile?.isLoggedInOtpUser ?? false,
      isNonVisaRecognizedUser: userProfile?.isNonVisaRecognizedUser ?? false,
      isOptimizedHalfSheetEligible:
        !isMobileSafari() &&
        isPhoneBrowser() &&
        (customFlowType === 'paymentsetting' || customFlowType === 'withincheckout'),
      isPrivacyPolicyPresent: Boolean(getComplianceResource('PRIVACY_POLICY')),
      isRecognizedUser: userProfile?.isRecognizedUser ?? false,
      isTncFlow: tncStatus === 'not_accepted',
      isUserAuthenticated: Boolean(getAccessToken()?.token),
      isUserConsentedPrefill: prefillData?.isUserConsentedPrefill ?? false,
      isVsbButtonless: getVsbFlow(),
      locale: getUserLocale() ?? 'en-US',
      lookupStatus: userProfile?.lookupStatus ?? null,
      mobileNumber: userProfile?.mobileNumber,
      tncStatus: isComplianceTncPassed ? 'accepted' : (tncStatus ?? 'accepted')
    },
    vInitRequest: getVInitRequest(),
    vIntentId: getVIntentId(),
    visitId: getVisitId()
  };

  if (prefillData?.isUserConsentedPrefill) {
    srcConfigs.userInfo.email = prefillData.email;
    srcConfigs.userInfo.firstName = prefillData.firstName;
    srcConfigs.userInfo.lastName = prefillData.lastName;
    srcConfigs.userInfo.phone = prefillData.phone;
  }

  return srcConfigs;
}

export function getPayloadTypeIndicatorCheckout(): PayloadTypeIndicator {
  const dpaTransactionOptions = getDpaTransactionOptions();
  const checkoutData = getCheckoutData();

  if (checkoutData?.vsbFlow && checkoutData.payloadTypeIndicatorCheckout) {
    return checkoutData.payloadTypeIndicatorCheckout;
  }

  if (dpaTransactionOptions?.payloadTypeIndicator) {
    return dpaTransactionOptions.payloadTypeIndicator;
  }

  const merchantConfig = getMerchantConfig();

  return merchantConfig.dataLevel === 'NONE' ? 'SUMMARY' : merchantConfig.dataLevel;
}

export function getStepUpIdentifier(authenticationMethodType?: string) {
  const putIntentResponse = getPutIntentResponse();

  if (putIntentResponse === null || !putIntentResponse.ok) {
    return null;
  }

  const decodedCheckoutResponse = decodeCheckoutResponse(putIntentResponse.data.checkoutResponse);

  if (!decodedCheckoutResponse) {
    return null;
  }

  const authenticationMethods: AuthenticationMethod[] =
    decodedCheckoutResponse.maskedCard?.digitalCardData?.authenticationMethods;

  for (const authMethod of authenticationMethods) {
    if (authMethod.authenticationMethodType === authenticationMethodType) {
      return authMethod.methodAttributes?.stepUpIdentifier;
    }
  }

  return null;
}

export function getIntentPayload(): SetIntentRequestPayload {
  const dpaTransactionOptions = getDpaTransactionOptions();
  const srcConfigs = getSrcConfigs();
  const merchantConfig = getMerchantConfig();
  const vInitRequest = srcConfigs.vInitRequest;

  return {
    buttonAction: getButtonAction() ?? 'CONTINUE',
    channel: srcConfigs.channel,
    countryCode: srcConfigs.countryCode,
    currencyCode: dpaTransactionOptions?.transactionAmount?.transactionCurrencyCode,
    enableThreeDS: isTrue(merchantConfig.threeDSSetup?.threeDSActive),
    eventSource: srcConfigs.eventSource,
    externalClientId: merchantConfig.externalClientId,
    locale: srcConfigs.locale,
    merchantConfig: {
      billingConstraints: merchantConfig.billingConstraints,
      datalevel: merchantConfig.dataLevel,
      displayName: merchantConfig.displayName,
      shippingConstraints: merchantConfig.shippingConstraints,
      threeDSSetup: merchantConfig.threeDSSetup,
      tokenizationSetup: merchantConfig.tokenizationSetup,
      websiteUrl: merchantConfig.websiteUrl
    },
    orderId: vInitRequest?.paymentRequest?.orderId,
    srciProfile: !getIsVisaSRCi(),
    total: vInitRequest?.total,
    visaCardsOnly: true
  };
}

export function getCardListPayload(idTokens: string[]): CardListRequestPayload {
  const apikey = getApiKey();
  const dpaTransactionOptions = getDpaTransactionOptions();
  const fullAccessToken = getFullAccessToken();
  const srcConfigs = getSrcConfigs();
  const merchantConfig = getMerchantConfig();
  const vInitRequest = srcConfigs.vInitRequest;
  const isTnc = getTncStatus() === 'not_accepted';
  const tokenServerState = getTokenServerState();
  const codeChallenge = getProofState().codeChallenge;

  function emvcoIze(
    localeConstraints: BillingConstraints | ShippingConstraints | undefined
  ): CountryCode[] | undefined {
    return localeConstraints?.acceptedRegions?.region.map(region => region.countryCode);
  }

  function emvcoIze3ds(threeDsOptions: ThreeDSSetupOptions | undefined): ThreeDsSetupEnumerated {
    if (threeDsOptions) {
      if (isTrue(threeDsOptions.threeDSActive)) {
        return 'SELF';
      } else if (threeDsOptions.threeDSSuppressChallenge) {
        return 'ONBEHALF';
      }
    }

    return 'NONE';
  }

  /* START GENAI@GHCOPILOT  */
  pushEvents({
    source: window,
    origin: window.location.origin,
    data: {
      E: 'VISA_UNKNOWN_EVENT',
      event: 'get-card-list-biometrics-enabled-device',
      fidoAuthEnabled: getIsFeatureToggleEnabled('fidoAuthEnabled'),
      isBiometricsEnabledDevice: getMeta().isBiometricsEnabledDevice,
      srciDpaId: getExternalClientId(),
      TY: 'PAYLOAD'
    }
  });
  /* END GENAI@GHCOPILOT  */

  /* START GENAI@GHCOPILOT */
  const cardListPayload: CardListRequestPayload = {
    dpaTransactionOptions: {
      ...(getIsFeatureToggleEnabled('enableCpfSupportBrazil') && {
        consumerNationalIdentifierRequested:
          dpaTransactionOptions?.consumerNationalIdentifierRequested
      }),
      customInputData: {
        ...(getIsFeatureToggleEnabled('fidoAuthEnabled') &&
          getMeta().isBiometricsEnabledDevice && {
            isBiometricsEnabledDevice: getMeta().isBiometricsEnabledDevice
          }),
        ...(getIsVdiDisabled() && {
          isVdiDisabled: getIsVdiDisabled()
        }),
        isSrciProfile: !getIsVisaSRCi(),
        payloadTypeIndicatorCheckout: getPayloadTypeIndicatorCheckout(),
        srciDpaId: getExternalClientId(),
        visaCardsOnly: true
      },
      dpaAcceptedBillingCountries:
        dpaTransactionOptions?.dpaAcceptedBillingCountries ??
        emvcoIze(merchantConfig.billingConstraints),
      dpaAcceptedShippingCountries:
        dpaTransactionOptions?.dpaAcceptedShippingCountries ??
        emvcoIze(merchantConfig.shippingConstraints),
      dpaBillingPreference: dpaTransactionOptions?.dpaBillingPreference,
      dpaLocale: srcConfigs.locale,
      dpaShippingPreference: dpaTransactionOptions?.dpaShippingPreference,
      merchantCountryCode: srcConfigs.countryCode,
      merchantOrderId: vInitRequest?.paymentRequest?.orderId,
      paymentOptions: dpaTransactionOptions?.paymentOptions,
      reviewAction: getButtonAction(),
      threeDsInputData: dpaTransactionOptions?.threeDsInputData,
      threeDsPreference: getThreeDsPreference() ?? emvcoIze3ds(merchantConfig.threeDSSetup),
      transactionAmount: {
        transactionCurrencyCode: vInitRequest?.paymentRequest?.currencyCode,
        transactionAmount: vInitRequest?.paymentRequest?.total
      }
    },
    srcDpaId: getSrcDpaId()
  };
  /* END GENAI@GHCOPILOT */

  if (apikey) {
    cardListPayload.srcClientId = apikey;
  }

  if (!fullAccessToken) {
    cardListPayload.idTokens = idTokens;
  }

  /* START GENAI@GHCOPILOT */
  if (getIsFeatureToggleEnabled('enableVpps')) {
    cardListPayload.serverState = tokenServerState || '';
    cardListPayload.codeChallenge = codeChallenge || '';
  }
  /* END GENAI@GHCOPILOT */

  if (isTnc) {
    cardListPayload.customInputData = { partialAuthorization: getAccessToken()?.token };
  }

  return cardListPayload;
}

export const getLaunchData = () => ({
  apikey: getApiKey(),
  correlationId: getCorrelationId(),
  dpaId: getInitData()?.srciDpaId || getSrcDpaId(),
  externalProfileId: getExternalProfileId(),
  integrated: getDcfExperience() === 'integrated',
  isSPCSupported: getIsSPCSupported(),
  isSplunkLoggingEnabled: getIsSplunkLoggingEnabled(),
  isVisaSRCi: getIsVisaSRCi(),
  sessionId: getSessionId(),
  visitId: getVisitId()
});

export async function getCheckoutEnrollPayload(): Promise<OrchestratorCheckoutEnrollRequest> {
  const checkoutData = getCheckoutData();
  const finalConsumer = getIsFeatureToggleEnabled('enableMobileAsId')
    ? {
        ...checkoutData?.consumer,
        mobileNumber: {
          countryCode: checkoutData?.consumer?.mobileNumber?.countryCode.replace(/^\+/, '') || '',
          phoneNumber: checkoutData?.consumer?.mobileNumber?.phoneNumber.replace(/^\+/, '') || ''
        }
      }
    : checkoutData?.consumer;

  const encryptedConsumer = await encryptData<Consumer>(finalConsumer);
  const encryptedBillingAddress = checkoutData?.encryptedBillingAddress;
  const dpaTransactionOptions = getDpaTransactionOptions();
  const srcDpaId = getSrcDpaId();
  const platformType = getPlatformType() === 'iOS' ? 'I_OS' : getPlatformType();

  const checkoutEnrollPayload: OrchestratorCheckoutEnrollRequest = {
    appInstance: {
      userAgent: getAppInstance()
    },
    assuranceData: checkoutData?.assuranceData,
    complianceSettings: checkoutData?.complianceSettings,
    dpaTransactionOptions: {
      ...dpaTransactionOptions,
      customInputData: {
        ...dpaTransactionOptions?.customInputData,
        payloadTypeIndicatorCheckout: getPayloadTypeIndicatorCheckout(),
        platformType,
        referenceURL: window.location.href,
        ...(getIsFeatureToggleEnabled('enableComboCardSupportBrazil') && {
          paymentCardTypeSelected:
            checkoutData?.dpaTransactionOptions?.customInputData?.paymentCardTypeSelected
        })
      }
    },
    encryptedCard: checkoutData?.encryptedCard,
    srcClientId: getApiKey(),
    srcCorrelationId: getVIntentId(),
    srciTransactionId: getCorrelationId()
  };

  if (!getIsFeatureToggleEnabled('orchestratorCheckoutEnrollCompliance')) {
    checkoutEnrollPayload.complianceSettings && delete checkoutEnrollPayload.complianceSettings;
  }

  if (srcDpaId) {
    checkoutEnrollPayload.srcDpaId = srcDpaId;
  }

  if (checkoutData?.idToken) {
    checkoutEnrollPayload.authorization = checkoutData.idToken;
  }

  if (encryptedConsumer) {
    checkoutEnrollPayload.encryptedConsumer = encryptedConsumer;
  }

  if (encryptedBillingAddress) {
    checkoutEnrollPayload.encryptedBillingAddress = encryptedBillingAddress;
  }

  return checkoutEnrollPayload;
}

export async function getIssuerConfigs() {
  let issuerLogoUri = '';
  let issuerName = '';
  let issuerLogoId = '';
  const cardList = getMaskedCards();
  const selectedCardId = getSelectedCardId();

  if (cardList && selectedCardId) {
    const selectedCard = cardList.find(card => card.srcDigitalCardId === selectedCardId);

    if (selectedCard) {
      issuerLogoUri =
        selectedCard.digitalCardFeatures?.find(feature => feature.contentType === 'IMAGE_URL')
          ?.content || '';

      issuerName = b64Encode(
        selectedCard.digitalCardFeatures?.find(feature => feature.contentType === 'TEXT_STRING')
          ?.content || ''
      );

      if (issuerLogoUri) {
        issuerLogoId = getIdFromUrl(issuerLogoUri);
      }
    }
  }

  return {
    issuerLogoUri,
    issuerName,
    issuerLogoId
  };
}

export async function getCardArtAndIssuerConfigs(
  authenticationMethod: AuthenticationMethod
): Promise<CardArtConfig> {
  const { issuerLogoUri, issuerName, issuerLogoId } = await getIssuerConfigs();

  return {
    appUrl: authenticationMethod.methodAttributes?.appURL,
    issuerLogoId,
    issuerLogoUri,
    issuerName,
    sessionId: getSessionId(),
    xAuthCode: getXAuthCode() || 'mock-x-auth-code'
    // local iss otp comment
    // xAuthCode: getXAuthCode()
  };
}

export async function getApp2AppPayload(
  authenticationMethod: AuthenticationMethod
): Promise<BankAppConfig> {
  if (!authenticationMethod.methodAttributes?.stepUpIdentifier) {
    const stepUpIdentifier = getStepUpIdentifier(authenticationMethod.authenticationMethodType);

    if (stepUpIdentifier) {
      authenticationMethod.methodAttributes = {
        ...authenticationMethod.methodAttributes,
        stepUpIdentifier
      };
    }
  }

  const { issuerLogoUri, issuerName, issuerLogoId } = await getIssuerConfigs();
  const payload = {
    apiKey: getApiKey(),
    authenticateRequestPayload: {
      authenticationMethod,
      authenticationSessionId: getVIntentId(),
      srcClientId: getApiKey(),
      srcCorrelationId: getVIntentId(),
      srciTransactionId: getCorrelationId()
    },
    clientId: getClientId() || '', // Provide a default value for getClientId()
    externalClientId: getExternalClientId(),
    intentId: getVIntentId() || '',
    issuerLogoId: issuerLogoId || '',
    issuerLogoUri: issuerLogoUri || '',
    issuerName: issuerName || '',
    sessionId: getSessionId(),
    // local iss otp comment
    xAuthCode: getXAuthCode() || ''
    // local iss otp uncomment
    // xAuthCode: getXAuthCode() || 'mock-x-auth-code'
  };

  return payload;
}

export function getAuthenticateRequestPayload(
  authenticateData: AuthenticateData
): AuthenticateApiRequestPayload {
  const methodAttributes = { ...authenticateData.authenticationMethod.methodAttributes };

  // Check to see if merchant data has stepUpIdentifier
  if (!authenticateData.authenticationMethod.methodAttributes?.stepUpIdentifier) {
    const stepUpIdentifier = getStepUpIdentifier(
      authenticateData.authenticationMethod.authenticationMethodType
    );

    if (stepUpIdentifier) {
      methodAttributes.stepUpIdentifier = stepUpIdentifier;
    }
  }

  return {
    authenticationContext: authenticateData.authenticationContext,
    authenticationMethod: { ...authenticateData.authenticationMethod, methodAttributes },
    authenticationSessionId: getVIntentId(),
    srcClientId: getApiKey(),
    srcCorrelationId: getVIntentId(),
    srciTransactionId: getCorrelationId()
  };
}

/* START GENAI@GHCOPILOT */
function getCustomInputDataMerchantName(): string {
  const dpaData = getCheckoutData()?.dpaData;
  const authenticationContext = getCheckoutData()?.authenticationContext;
  const dpaTransactionOptions = getDpaTransactionOptions();

  return (
    dpaData?.dpaName ??
    dpaData?.dpaPresentationName ??
    dpaData?.merchantName ??
    dpaTransactionOptions?.merchantName ??
    authenticationContext?.dpaData?.dpaName ??
    authenticationContext?.dpaData?.dpaPresentationName ??
    authenticationContext?.dpaData?.merchantName ??
    authenticationContext?.merchantName ??
    ''
  );
}

/* END GENAI@GHCOPILOT */

export async function getSelectCardPutIntentPayload(
  selectedCardId: string,
  meta?: CheckoutRequestMetadata
): Promise<PutIntentRequestPayload> {
  const checkoutData = getCheckoutData();
  const dpaTransactionOptionsFromInit = getDpaTransactionOptions();
  const initData = getInitData();
  const tokenServerState = getTokenServerState();
  const codeChallenge = getProofState().codeChallenge;
  let methodAttributes: MethodAttributes = {};

  if (
    getIsFeatureToggleEnabled('enabledIntegratedIssuerFlow') &&
    checkoutData?.authenticationMethod
  ) {
    methodAttributes = { ...checkoutData.authenticationMethod.methodAttributes };

    // Check to see if merchant data has stepUpIdentifier
    if (!checkoutData.authenticationMethod.methodAttributes?.stepUpIdentifier) {
      const stepUpIdentifier = getStepUpIdentifier(
        checkoutData.authenticationMethod.authenticationMethodType
      );

      if (stepUpIdentifier !== null) {
        methodAttributes.stepUpIdentifier = stepUpIdentifier;
      }
    }
  }

  const putIntentReqPayload: PutIntentRequestPayload = {
    assuranceData: checkoutData?.assuranceData,
    authenticationContext: checkoutData?.authenticationContext,
    authenticationMethod:
      getIsFeatureToggleEnabled('enabledIntegratedIssuerFlow') && checkoutData?.authenticationMethod
        ? { ...checkoutData.authenticationMethod, methodAttributes }
        : checkoutData?.authenticationMethod,
    dfpSessionId: getSessionId(),
    encryptedBillingAddress: checkoutData?.encryptedBillingAddress,
    paymentInstrumentId: selectedCardId,
    platformType: getPlatformType(),
    referenceURL: window.location.href,
    srciTransactionId: getCorrelationId()
  };

  if (putIntentReqPayload.authenticationContext?.dpaTransactionOptions) {
    //authenticationContext.dpaTransactionOptions can be dropped and don't need to be included in the Put Intent API payload
    const authenticationContext = { ...putIntentReqPayload.authenticationContext };
    delete authenticationContext.dpaTransactionOptions;
    putIntentReqPayload.authenticationContext = authenticationContext;
  }

  if (
    getIsFeatureToggleEnabled('enableIntegratedThreeDsDto') &&
    checkoutData?.authenticationContext &&
    checkoutData.authenticationMethod
  ) {
    putIntentReqPayload.dpaTransactionOptions = {
      merchantCategoryCode:
        checkoutData.dpaTransactionOptions?.merchantCategoryCode ||
        dpaTransactionOptionsFromInit?.merchantCategoryCode,
      merchantOrderId:
        dpaTransactionOptionsFromInit?.merchantOrderId ||
        checkoutData.dpaTransactionOptions?.merchantOrderId,
      numberOfPayments: checkoutData.dpaTransactionOptions?.numberOfPayments,
      purchaseDate:
        checkoutData.dpaTransactionOptions?.purchaseDate ||
        dpaTransactionOptionsFromInit?.purchaseDate,
      recurringEndDate:
        checkoutData.dpaTransactionOptions?.recurringEndDate ||
        dpaTransactionOptionsFromInit?.recurringEndDate,
      recurringFrequency:
        checkoutData.dpaTransactionOptions?.recurringFrequency ||
        dpaTransactionOptionsFromInit?.recurringFrequency,
      transactionAmount:
        checkoutData.dpaTransactionOptions?.transactionAmount ||
        dpaTransactionOptionsFromInit?.transactionAmount,
      transactionInstruction:
        checkoutData.dpaTransactionOptions?.transactionInstruction ||
        dpaTransactionOptionsFromInit?.transactionInstruction
    };
  }

  //acsWindowSize value is added onto customInputData to help potential 3DS stepups in integrated select card flows
  const dcfExperience = getDcfExperience();
  const isIntegrated = dcfExperience === 'integrated';

  if (isIntegrated && meta?.acsWindowSize) {
    let acsWindowSize = meta.acsWindowSize;

    //For FIDO flows - if meta.sdkPopupAcsWindowSize is present, windowRef iframe will be ignored and SDK will create the popup
    if (
      getIsFeatureToggleEnabled('fidoAuthEnabled') &&
      isSelectedCardEligibleForFido(selectedCardId) &&
      meta.sdkPopupAcsWindowSize
    ) {
      acsWindowSize = meta.sdkPopupAcsWindowSize;
    }

    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      customInputData: {
        ...putIntentReqPayload.dpaTransactionOptions?.customInputData,
        acsWindowSize
      }
    };
  }

  if (
    isIntegrated &&
    getIsFeatureToggleEnabled('fidoAuthEnabled') &&
    getMeta().isBiometricsEnabledDevice
  ) {
    putIntentReqPayload.isBiometricsEnabledDevice = getMeta().isBiometricsEnabledDevice;
  }

  if (getIsFeatureToggleEnabled('enableDaf')) {
    const authenticatedCredentialRequested =
      checkoutData?.dpaTransactionOptions?.authenticatedCredentialRequested ||
      dpaTransactionOptionsFromInit?.authenticatedCredentialRequested;

    if (authenticatedCredentialRequested) {
      putIntentReqPayload.dpaTransactionOptions = {
        ...putIntentReqPayload.dpaTransactionOptions,
        authenticatedCredentialRequested
      };

      if (checkoutData?.shippingAddress && isAddressValidated(checkoutData.shippingAddress)) {
        putIntentReqPayload.encryptedShippingAddress = await encryptData<ShippingAddress>(
          checkoutData.shippingAddress
        );
      } else {
        putIntentReqPayload.shippingAddressId = checkoutData?.shippingAddressId;
      }
    }
  }

  if (getIsFeatureToggleEnabled('enableCpfSupportBrazil')) {
    const taxIdData = getConsumerNationalIdentiferData();
    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      ...taxIdData.dpaTransactionOptions
    };

    putIntentReqPayload.consumerNationalIdentifier = taxIdData.consumerNationalIdentifier;
  }

  if (getIsFeatureToggleEnabled('enableComboCardSupportBrazil')) {
    const paymentInstrumentType =
      checkoutData?.dpaTransactionOptions?.customInputData?.paymentCardTypeSelected;

    putIntentReqPayload.paymentInstrumentType = paymentInstrumentType;
  }

  /* START GENAI@GHCOPILOT
    visa-sdk will pass acquirerBIN, acquirerMerchantId, and merchantName to the putIntent payload
    only if they are all present in the checkoutData
  END GENAI@GHCOPILOT */
  if (
    checkoutData?.dpaTransactionOptions?.acquirerBIN &&
    checkoutData.dpaTransactionOptions.acquirerMerchantId &&
    checkoutData.dpaTransactionOptions.merchantName
  ) {
    // START GENAI@GHCOPILOT
    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      acquirerBIN: checkoutData.dpaTransactionOptions.acquirerBIN,
      acquirerMerchantId: checkoutData.dpaTransactionOptions.acquirerMerchantId,
      merchantName: checkoutData.dpaTransactionOptions.merchantName,
      transactionAmount: checkoutData.dpaTransactionOptions.transactionAmount
    };
    // END GENAI@GHCOPILOT
  }

  /* START GENAI@GHCOPILOT */
  if (getDpaLocale() && getIsFeatureToggleEnabled('enableVpps')) {
    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      dpaLocale:
        checkoutData?.dpaTransactionOptions?.dpaLocale || dpaTransactionOptionsFromInit?.dpaLocale
    };
  }
  /* END GENAI@GHCOPILOT */

  if (initData?.customData?.isVsb) {
    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      payloadTypeIndicatorCheckout: getPayloadTypeIndicatorCheckout()
    };
  }

  //START GENAI@GHCOPILOT
  //If the checkoutData contains the vsbFlow flag, the dpaTransactionOptions will be added to the putIntent payload

  if (checkoutData?.vsbFlow) {
    putIntentReqPayload.dpaTransactionOptions = checkoutData.dpaTransactionOptions;
  }
  //END GENAI@GHCOPILOT

  //remove empty object
  if (
    putIntentReqPayload.dpaTransactionOptions?.customInputData &&
    !Object.keys(putIntentReqPayload.dpaTransactionOptions.customInputData).length
  ) {
    delete putIntentReqPayload.dpaTransactionOptions.customInputData;
  }

  if (
    putIntentReqPayload.dpaTransactionOptions &&
    !Object.keys(putIntentReqPayload.dpaTransactionOptions).length
  ) {
    delete putIntentReqPayload.dpaTransactionOptions;
  }

  /* START GENAI@GHCOPILOT */

  /* START GENAI@GHCOPILOT */
  if (getIsFeatureToggleEnabled('enableVpps')) {
    putIntentReqPayload.serverState = tokenServerState || '';
    putIntentReqPayload.codeChallenge = codeChallenge || '';
  }
  /* END GENAI@GHCOPILOT */

  /* START GENAI@GHCOPILOT */
  putIntentReqPayload.dpaTransactionOptions = {
    ...putIntentReqPayload.dpaTransactionOptions,
    customInputData: {
      ...putIntentReqPayload.dpaTransactionOptions?.customInputData,
      /* START GENAI@GHCOPILOT */
      browserDetails: {
        browserColorDepth: window.screen.colorDepth,
        browserLanguage: navigator.language || 'en-US',
        browserScreenHeight: window.screen.height,
        browserScreenWidth: window.screen.width,
        browserTimeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      /* END GENAI@GHCOPILOT */
      isVdiDisabled: getIsVdiDisabled(),
      merchantName: getCustomInputDataMerchantName(),
      topOrigin:
        checkoutData?.dpaData?.dpaUri ??
        checkoutData?.authenticationContext?.dpaData?.dpaUri ??
        initData?.dpaData?.dpaUri ??
        getTopOrigin()
    }
  };
  /* END GENAI@GHCOPILOT */

  /* END GENAI@GHCOPILOT */

  //START GENAI@GHCOPILOT

  if (getIsFeatureToggleEnabled('orchestratorCheckoutApi')) {
    putIntentReqPayload.authorization = checkoutData?.idToken;
    putIntentReqPayload.complianceSettings = checkoutData?.complianceSettings;
    putIntentReqPayload.srcClientId = getApiKey();
    putIntentReqPayload.srcCorrelationId = getVIntentId();
    putIntentReqPayload.srcDigitalCardId = selectedCardId;
    putIntentReqPayload.platformType = getPlatformType() === 'iOS' ? 'I_OS' : getPlatformType();

    //START GENAI@GHCOPILOT
    //START GENAI@GHCOPILOT
    putIntentReqPayload.dpaTransactionOptions = {
      ...putIntentReqPayload.dpaTransactionOptions,
      customInputData: {
        ...putIntentReqPayload.dpaTransactionOptions.customInputData,
        isVsbButtonless: getVsbFlow(),
        isOrchestratorCheckoutApi: true
      }
    };

    // Rename verificationData to verificationDatas in assuranceData if it exists
    if (putIntentReqPayload.assuranceData?.verificationData) {
      // Create a new object with all existing properties
      const newAssuranceData = { ...putIntentReqPayload.assuranceData };

      // Add the verificationDatas property with the value from verificationData
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      (newAssuranceData as any).verificationDatas =
        putIntentReqPayload.assuranceData.verificationData;

      // Remove the original verificationData property
      delete newAssuranceData.verificationData;

      // Replace the assuranceData object
      putIntentReqPayload.assuranceData = newAssuranceData;
    }
    //END GENAI@GHCOPILOT
  }
  //END GENAI@GHCOPILOT

  return putIntentReqPayload;
}

export function getAssuranceDataForThreeDs(threeDsCompletionStatus: string): AssuranceData {
  const authenticationSubject = getCheckoutData()?.authenticationMethod?.authenticationSubject;
  const isOrchestratorCheckoutApiEnabled = getIsFeatureToggleEnabled('orchestratorCheckoutApi');

  const verificationData = [
    {
      verificationEntity: '01', // SRC Initiator
      verificationEvents: ['01'], // Payment Transaction
      verificationMethod: '01', // Use of an EMV 3-D Secure ACS
      verificationResults:
        threeDsCompletionStatus === 'approved'
          ? '01'
          : threeDsCompletionStatus === 'declined'
            ? '02'
            : '03', // 01 = Verified, 02 = Not Verified, 03 = Not performed
      verificationTimestamp: `${Math.floor(Date.now() / 1000)}`,
      verificationType: authenticationSubject
    }
  ];

  return {
    [isOrchestratorCheckoutApiEnabled ? 'verificationDatas' : 'verificationData']: verificationData
  } as any; //eslint-disable-line @typescript-eslint/no-explicit-any
}

//END GENAI@GHCOPILOT

export function getConsumerNationalIdentiferData(): ConsumerNationalIdentiferData {
  const checkoutData = getCheckoutData();
  const dpaTransactionOptions = getDpaTransactionOptions();
  const consumerNationalIdentifierRequested =
    dpaTransactionOptions?.consumerNationalIdentifierRequested;

  const consumerNationalIdentifier = checkoutData?.consumer?.nationalIdentifier;

  return {
    dpaTransactionOptions: consumerNationalIdentifierRequested
      ? { consumerNationalIdentifierRequested }
      : undefined,
    consumerNationalIdentifier: consumerNationalIdentifier ?? undefined
  };
}

export function getCheckoutRequestMetadata() {
  return getState().meta.checkoutRequestMetadata;
}

export function getFidoConfig(): FidoConfig {
  const putIntentResponse = getPutIntentResponse();

  // send issuer logo and card art urls in getFidoConfig postmessage
  const cardList = getMaskedCards();
  const selectedCardId = getSelectedCardId();
  const checkoutData = getCheckoutData();
  const dpaTransactionOptionsFromInit = getDpaTransactionOptions();
  const user = getUser();
  const isSplit = getState().meta.split;

  let cardArtUri = '';
  let issuerLogoUri = '';
  let cardArtId = '';
  let issuerLogoId = '';
  let issuerName = '';
  let consumerDataIssuerManaged = false;

  if (cardList && selectedCardId) {
    const selectedCard = cardList.find(card => card.srcDigitalCardId === selectedCardId);

    if (selectedCard) {
      cardArtUri = selectedCard.digitalCardData.artUri;
      issuerLogoUri =
        selectedCard.digitalCardFeatures?.find(feature => feature.contentType === 'IMAGE_URL')
          ?.content || '';

      if (cardArtUri) {
        cardArtId = getIdFromUrl(cardArtUri);
      }

      if (issuerLogoUri) {
        issuerLogoId = getIdFromUrl(issuerLogoUri);
      }

      issuerName = b64Encode(
        selectedCard.digitalCardFeatures?.find(feature => feature.contentType === 'TEXT_STRING')
          ?.content || ''
      );
    }
  }

  if (putIntentResponse?.ok) {
    consumerDataIssuerManaged =
      // @ts-ignore
      putIntentResponse.data.maskedCard?.consumerDataIssuerManaged ?? false;
  }

  const merchantConfig = getMerchantConfig();

  const idType = getIdentityType();

  // fido config data
  return {
    amount:
      checkoutData?.dpaTransactionOptions?.transactionAmount?.transactionAmount ||
      dpaTransactionOptionsFromInit?.transactionAmount?.transactionAmount,
    cardArtId,
    cardArtUri,
    consumerDataIssuerManaged,
    currency:
      checkoutData?.dpaTransactionOptions?.transactionAmount?.transactionCurrencyCode ||
      dpaTransactionOptionsFromInit?.transactionAmount?.transactionCurrencyCode,
    debuggingEnabled: Boolean(getIsFeatureToggleEnabled('debuggingEnabled')),
    dpaLocale:
      dpaTransactionOptionsFromInit?.dpaLocale || checkoutData?.dpaTransactionOptions?.dpaLocale,
    email: getIsFeatureToggleEnabled('enableMobileAsId')
      ? ((idType === 'EMAIL_ADDRESS' ? getUserEmail() : getUserPhone()) ?? '')
      : (getUserEmail() ?? ''),
    enableNewStepupDesign: getIsFeatureToggleEnabled('enableNewStepupDesign'),
    intentId: getVIntentId() ?? '',
    isRecognized: isSplit ? true : (user?.isRecognizedUser ?? false),
    issuerLogoId,
    issuerLogoUri,
    issuerName,
    merchantCountryCode:
      dpaTransactionOptionsFromInit?.merchantCountryCode ||
      checkoutData?.dpaTransactionOptions?.merchantCountryCode,
    // in 23.09.10 authenticationContext merchantName should be sent per integration guide
    merchantName: b64Encode(
      (checkoutData?.authenticationContext?.merchantName || merchantConfig.displayName) ?? ''
    ),
    paymentInstrumentId: getSelectedCardId(),
    sessionId: getSessionId(),
    sessionQualifier: putIntentResponse?.headers['x-iam-session-qualifier'],
    threeDsUrl: getThreeDsUrl(),
    transactionInstruction:
      checkoutData?.dpaTransactionOptions?.transactionInstruction ||
      dpaTransactionOptionsFromInit?.transactionInstruction ||
      'ONETIME',
    xAuthCode: getXAuthCode()
  };
}

export async function getCvvConfig(): Promise<CvvConfig> {
  return {
    apiKey: getApiKey(),
    clientId: getClientId() || '', // Provide a default value for getClientId()
    externalClientId: getExternalClientId(),
    intentId: getVIntentId() ?? '',
    putIntentRequestPayload: await getSelectCardPutIntentPayload(
      getSelectedCardId(),
      getCheckoutRequestMetadata()
    ),
    sessionId: getSessionId(),
    xAuthCode: getXAuthCode()
  };
}

export function isSelectedCardEligibleForFido(selectedCardId: string) {
  const cardList = getMaskedCards();

  let isCardBiometricsEligible = false;

  if (cardList && selectedCardId) {
    const selectedCard = cardList.find(card => card.srcDigitalCardId === selectedCardId);

    if (selectedCard) {
      isCardBiometricsEligible = selectedCard.isBiometricsEligible;
    }
  }

  return isCardBiometricsEligible;
}

export function isTransactionFidoEligible(): boolean {
  const checkoutData = getState().config.checkout;

  const isTransactionAuthentication =
    checkoutData?.authenticationContext?.authenticationReasons?.some(
      authenticationReason => authenticationReason === 'TRANSACTION_AUTHENTICATION'
    );

  const isEmvco13Authenticated =
    checkoutData?.authenticationMethod?.authenticationMethodType === 'MANAGED_AUTHENTICATION';

  const isEmvco14Authentciated =
    checkoutData?.dpaTransactionOptions?.authenticationPreferences?.payloadRequested ===
    'AUTHENTICATED';

  const isAuthenticationRequested =
    (isTransactionAuthentication && isEmvco13Authenticated) || isEmvco14Authentciated;

  if (isAuthenticationRequested) {
    if (getIsFeatureToggleEnabled('fidoAuthEnabled')) {
      return (
        getState().meta.isBiometricsEnabledDevice &&
        isSelectedCardEligibleForFido(getSelectedCardId())
      );
    }
  }

  return false;
}

export function getMerchantCountryCode(): string | undefined {
  const dpaTransactionOptionsFromInit = getDpaTransactionOptions();
  const checkoutData = getCheckoutData();

  return (
    dpaTransactionOptionsFromInit?.merchantCountryCode ||
    checkoutData?.dpaTransactionOptions?.merchantCountryCode
  );
}

export function getDpaLocale(): string | undefined {
  const dpaTransactionOptionsFromInit = getDpaTransactionOptions();
  const checkoutData = getCheckoutData();
  return dpaTransactionOptionsFromInit?.dpaLocale || checkoutData?.dpaTransactionOptions?.dpaLocale;
}

//START GENAI@GHCOPILOT
export function getVsbFlow(): boolean {
  return getMeta().integrationType === 'vsb-buttonless';
}

//END GENAI@GHCOPILOT

export async function getIssuerBaseData(
  authenticationMethod: AuthenticationMethod
): Promise<IssuerOTPRequestConfigs> {
  const methodAttributes = { ...authenticationMethod.methodAttributes };
  const { issuerLogoUri } = await getIssuerConfigs();

  // Check to see if merchant data has stepUpIdentifier
  if (!authenticationMethod.methodAttributes?.stepUpIdentifier) {
    const stepUpIdentifier = getStepUpIdentifier(authenticationMethod.authenticationMethodType);

    if (stepUpIdentifier) {
      methodAttributes.stepUpIdentifier = stepUpIdentifier;
    }
  }

  return {
    apiKey: getApiKey(),
    authenticateRequestPayload: {
      authenticationMethod: { ...authenticationMethod, methodAttributes },
      authenticationSessionId: getVIntentId(),
      srcClientId: getApiKey(),
      srcCorrelationId: getVIntentId(),
      srciTransactionId: getCorrelationId()
    },
    intentId: getVIntentId() ?? '',
    issuerLogoUri,
    putIntentRequestPayload: await getSelectCardPutIntentPayload(
      getSelectedCardId(),
      getCheckoutRequestMetadata()
    ),
    sessionId: getSessionId(),
    srciTransactionId: getCorrelationId(),
    xAuthCode: getXAuthCode() || 'mock-x-auth-code'
  };
}
